// Sales opportunities / pipeline data layer.
// Deterministic: same opportunity id yields the same attributes across reloads.

import { BRANCHES } from './orgData';

// ------------ Seeded PRNG ------------
const hashSeed = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const mulberry32 = (a) => () => {
  a = (a + 0x6D2B79F5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const rng = (seedStr) => mulberry32(hashSeed(seedStr));
const pick = (r, arr) => arr[Math.floor(r() * arr.length)];
const range = (r, lo, hi) => lo + r() * (hi - lo);
const intRange = (r, lo, hi) => Math.floor(range(r, lo, hi + 1));

// ------------ Catalogs ------------
export const STAGES = [
  { id: 'lead',        name: 'Lead',        probability: 0.10, weight: 0.30, tone: 'neutral' },
  { id: 'qualified',   name: 'Qualified',   probability: 0.25, weight: 0.25, tone: 'info'    },
  { id: 'quoted',      name: 'Quoted',      probability: 0.45, weight: 0.20, tone: 'info'    },
  { id: 'negotiating', name: 'Negotiating', probability: 0.70, weight: 0.15, tone: 'warn'    },
  { id: 'booked',      name: 'Booked',      probability: 0.95, weight: 0.10, tone: 'ok'      },
];

export const OPEN_STAGE_IDS = STAGES.filter((s) => s.id !== 'booked').map((s) => s.id);

export const CUSTOMER_SEGMENTS = [
  { id: 'retail',      name: 'Retail',              share: 0.42, valueLo: 1500,   valueHi: 9500   },
  { id: 'fleet',       name: 'Commercial Fleet',    share: 0.18, valueLo: 85000,  valueHi: 820000 },
  { id: 'insurer',     name: 'Insurer Panel',       share: 0.14, valueLo: 320000, valueHi: 2100000 },
  { id: 'dealer',      name: 'Dealer Partner',      share: 0.12, valueLo: 54000,  valueHi: 410000 },
  { id: 'builder',     name: 'Builder / Construct', share: 0.08, valueLo: 42000,  valueHi: 540000 },
  { id: 'government',  name: 'Government',          share: 0.06, valueLo: 180000, valueHi: 1200000 },
];

export const SOURCES = [
  { id: 'insurer-referral', name: 'Insurer Referral', share: 0.30 },
  { id: 'broker',           name: 'Broker',           share: 0.12 },
  { id: 'direct',           name: 'Direct',           share: 0.14 },
  { id: 'website',          name: 'Website',          share: 0.18 },
  { id: 'walk-in',          name: 'Walk-in',          share: 0.10 },
  { id: 'fleet-account',    name: 'Fleet Account',    share: 0.10 },
  { id: 'cross-sell',       name: 'Cross-sell',       share: 0.06 },
];

export const LOST_REASONS = [
  { id: 'price',              name: 'Price',              share: 0.32, tone: 'alert' },
  { id: 'timing',             name: 'Timing',             share: 0.15, tone: 'warn'  },
  { id: 'availability',       name: 'Parts Availability', share: 0.14, tone: 'warn'  },
  { id: 'competitor',         name: 'Competitor Won',     share: 0.22, tone: 'alert' },
  { id: 'insurer-preference', name: 'Insurer Preference', share: 0.10, tone: 'warn'  },
  { id: 'no-decision',        name: 'No Decision',        share: 0.07, tone: 'neutral' },
];

export const PRODUCT_LINES = [
  'Windscreen', 'Side Glass', 'Rear Screen', 'Sunroof', 'ADAS Recalibration',
  'Regas / Aircon', 'Window Tint', 'Body Glass', 'Architectural Glazing',
  'Insurance Glass Claim', 'Fleet Package', 'Mirror Replacement',
];

const CUSTOMER_NAMES = {
  retail: ['M. Dlamini', 'J. Pretorius', 'N. Jacobs', 'R. Govender', 'S. Khoza', 'T. van der Merwe',
    'A. Naidoo', 'P. Mthembu', 'L. Botha', 'K. Moloi', 'V. Patel', 'C. Steyn', 'B. Oosthuizen',
    'F. Abrahams', 'D. Banda', 'H. Kruger', 'Y. Desai', 'O. Chetty', 'E. Davids', 'M. Mokoena'],
  fleet: ['Avis Fleet Services', 'Bidvest Bank Vehicle Services', 'Super Group Fleet', 'Barloworld Logistics',
    'Imperial Logistics', 'DSV South Africa', 'Value Logistics', 'Unitrans', 'RTT Group',
    'Eqstra Fleet Management', 'First Car Rental', 'Europcar Fleet', 'Budget Rent A Car SA',
    'Innovation Group Fleet', 'Pargo Commercial'],
  insurer: ['Santam Panel Contract', 'Discovery Insure Programme', 'OUTsurance Glass',
    'Hollard Broker Scheme', 'Old Mutual Insure Panel', 'Auto & General Direct',
    'Bryte Commercial', 'Momentum Short-Term', 'King Price Direct', 'MiWay Panel',
    'Mutual & Federal', 'Absa Insurance Panel', 'Standard Insurance Contract'],
  dealer: ['Motus Holdings Group', 'CMH Honda Group', 'McCarthy Toyota Network',
    'Super Group Dealerships', 'Williams Hunt Group', 'NMI Durban Group', 'Barloworld Motor',
    'Tavcor Motors', 'Hatfield Motor Group', 'Cargo Motors Dealer Network'],
  builder: ['WBHO Construction', 'Murray & Roberts Buildings', 'Group Five Projects',
    'Stefanutti Stocks', 'Aveng Grinaker-LTA', 'Raubex Building', 'Power Construction',
    'Concor Construction', 'Basil Read Buildings', 'Tiber Construction', 'GVK-Siya Zama'],
  government: ['SAPS Vehicle Fleet', 'Dept of Transport Fleet', 'Gauteng Provincial Govt',
    'City of Cape Town Fleet', 'eThekwini Metro Fleet', 'City of Tshwane Fleet',
    'Dept of Correctional Services', 'Dept of Public Works', 'SANDF Logistics'],
};

const REP_FIRST = ['Thandi', 'Lerato', 'Sipho', 'Busisiwe', 'Johan', 'Pieter', 'Ayesha',
  'Priya', 'Deshen', 'Michelle', 'Karabo', 'Zanele', 'Rian', 'Francois', 'Dineo',
  'Nkosi', 'Imran', 'Yusuf', 'Carla', 'Tshepo', 'Bongani', 'Nadia', 'Jason',
  'Jessica', 'Mpho', 'Liam', 'Keanu', 'Aisha', 'Tanya', 'Siphesihle', 'Leon',
  'Melissa', 'Ruan', 'Hanno', 'Unathi'];
const REP_LAST = ['Khumalo', 'Nkosi', 'Dlamini', 'Mthembu', 'Mokoena', 'van der Merwe',
  'Pretorius', 'du Plessis', 'Naidoo', 'Pillay', 'Govender', 'Moodley', 'Abrahams',
  'Davids', 'Jacobs', 'Banda', 'Kamau', 'Chirwa', 'Mwangi', 'Matsinhe', 'Patel',
  'Desai', 'Oosthuizen', 'Steyn', 'Botha', 'Kruger', 'October'];

// ------------ REPS ------------
// Spread reps across branches — flagship 3, standard 2, regional 1, Africa-metro 1-2.
const repsPerBranch = (b) => ({
  'Metro-Flagship': 3,
  'Metro-Standard': 2,
  'Regional-Town':  1,
  'Africa-Metro':   2,
}[b.peerGroup] || 1);

function buildReps() {
  const out = [];
  let seq = 1;
  for (const b of BRANCHES) {
    const r = rng(`reps::${b.id}`);
    const n = repsPerBranch(b);
    for (let i = 0; i < n; i++) {
      const first = pick(r, REP_FIRST);
      const last  = pick(r, REP_LAST);
      out.push({
        id: `REP-${String(seq).padStart(3, '0')}`,
        name: `${first} ${last}`,
        branchId: b.id,
        branchName: b.name,
      });
      seq++;
    }
  }
  return out;
}

export const REPS = buildReps();
const REPS_BY_BRANCH = REPS.reduce((acc, r) => {
  if (!acc[r.branchId]) acc[r.branchId] = [];
  acc[r.branchId].push(r);
  return acc;
}, {});

// ------------ Stage weighted pick ------------
const pickWeighted = (r, items, key = 'share') => {
  const roll = r();
  let acc = 0;
  return items.find((it) => (acc += it[key]) >= roll) || items[0];
};

// ------------ OPPORTUNITIES ------------
// ~250 total — heavier in flagships, lighter in regional.
const dealsPerBranch = (b) => ({
  'Metro-Flagship': 22,
  'Metro-Standard': 14,
  'Regional-Town':  7,
  'Africa-Metro':   11,
}[b.peerGroup] || 10);

const now = Date.now();
const DAY = 24 * 60 * 60 * 1000;

function buildOpportunities() {
  const out = [];
  let seq = 4200; // looks like "OPP-2026-4200+"
  for (const b of BRANCHES) {
    const count = dealsPerBranch(b);
    for (let i = 0; i < count; i++) {
      seq++;
      const id = `OPP-2026-${seq}`;
      const r = rng(`opp::${id}`);

      const seg = pickWeighted(r, CUSTOMER_SEGMENTS);
      const stage = pickWeighted(r, STAGES, 'weight');
      const source = pickWeighted(r, SOURCES);
      const customer = pick(r, CUSTOMER_NAMES[seg.id] || CUSTOMER_NAMES.retail);
      const reps = REPS_BY_BRANCH[b.id] || REPS;
      const rep = pick(r, reps);

      // Value skewed — more small deals, fewer huge
      const valBase = range(r, seg.valueLo, seg.valueHi);
      const value = Math.round(valBase * (r() < 0.15 ? 1.6 : 1)); // occasional "whale"

      const daysInStage = intRange(r, 1, stage.id === 'booked' ? 3 : 28);
      const daysSinceActivity = intRange(r, 0, stage.id === 'booked' ? 2 : 22);
      const createdDaysAgo = intRange(r, daysInStage, daysInStage + 45);
      const expectedCloseDaysAhead = stage.id === 'booked'
        ? intRange(r, -3, 2)
        : intRange(r, 2, 80);

      const probabilityJitter = +Math.max(0.05, Math.min(0.98, stage.probability + (r() - 0.5) * 0.12)).toFixed(2);

      // Product mix — 1-3 lines
      const pmCount = 1 + intRange(r, 0, Math.min(2, Math.floor(value / 120000)));
      const productMix = [];
      const pool = [...PRODUCT_LINES];
      for (let k = 0; k < pmCount; k++) {
        const idx = Math.floor(r() * pool.length);
        productMix.push(pool.splice(idx, 1)[0]);
      }

      // Risk flags
      const isStalled = daysSinceActivity > 14 && stage.id !== 'booked';
      const isBigTicket = value >= 400000;
      const isAtRisk = isStalled || (stage.id === 'negotiating' && daysInStage > 18);

      // Won/lost resolution for booked or recently-resolved deals
      let outcome = null;
      let lostReason = null;
      if (stage.id === 'booked') {
        outcome = 'won';
      } else if (r() < 0.12) {
        // some deals have already closed lost — flagged separately for analysis
        outcome = 'lost';
        lostReason = pickWeighted(r, LOST_REASONS).id;
      }

      out.push({
        id,
        branchId: b.id,
        branchName: b.name,
        region: b.region,
        country: b.country,
        name: buildDealName(r, seg, customer),
        customer,
        customerSegmentId: seg.id,
        customerSegmentName: seg.name,
        stageId: stage.id,
        stageName: stage.name,
        stageTone: stage.tone,
        sourceId: source.id,
        sourceName: source.name,
        value,
        weightedValue: Math.round(value * probabilityJitter),
        probability: probabilityJitter,
        daysInStage,
        daysSinceActivity,
        createdDaysAgo,
        expectedCloseDaysAhead,
        expectedCloseDate: new Date(now + expectedCloseDaysAhead * DAY).toISOString().slice(0, 10),
        createdDate: new Date(now - createdDaysAgo * DAY).toISOString().slice(0, 10),
        lastActivityDate: new Date(now - daysSinceActivity * DAY).toISOString().slice(0, 10),
        productMix,
        repId: rep.id,
        repName: rep.name,
        outcome,   // 'won' | 'lost' | null
        lostReason,
        isStalled,
        isBigTicket,
        isAtRisk,
      });
    }
  }
  return out;
}

function buildDealName(r, seg, customer) {
  if (seg.id === 'retail') {
    const items = ['windscreen replace', 'insurance glass claim', 'side glass + regas', 'ADAS recalibration', 'chip repair'];
    return `${customer} — ${pick(r, items)}`;
  }
  if (seg.id === 'fleet') {
    return `${customer} — Q${intRange(r, 1, 4)} fleet programme`;
  }
  if (seg.id === 'insurer') {
    const quarters = ['H1', 'H2', 'Q2', 'Q3', 'annual'];
    return `${customer} — ${pick(r, quarters)} renewal`;
  }
  if (seg.id === 'dealer') {
    return `${customer} — OEM panel programme`;
  }
  if (seg.id === 'builder') {
    const sites = ['CBD tower', 'Waterfront block', 'ML Sultan campus', 'Loftus precinct', 'N1 business park', 'Umhlanga ridge', 'Sandhurst estate'];
    return `${customer} — ${pick(r, sites)} glazing`;
  }
  if (seg.id === 'government') {
    return `${customer} — tender ${intRange(r, 2024, 2026)}/${intRange(r, 1, 40)}`;
  }
  return `${customer} — opportunity`;
}

export const OPPORTUNITIES = buildOpportunities();

// ------------ Queries ------------
export const opportunitiesByBranch = (branchId) => OPPORTUNITIES.filter((o) => o.branchId === branchId);
export const openOpportunities = (items = OPPORTUNITIES) => items.filter((o) => OPEN_STAGE_IDS.includes(o.stageId) && o.outcome !== 'lost');

// ------------ Rep rollup ------------
function rollupRep(rep, items = OPPORTUNITIES) {
  const mine = items.filter((o) => o.repId === rep.id);
  const won  = mine.filter((o) => o.outcome === 'won');
  const lost = mine.filter((o) => o.outcome === 'lost');
  const open = mine.filter((o) => OPEN_STAGE_IDS.includes(o.stageId) && o.outcome !== 'lost');
  return {
    ...rep,
    dealsOwned: mine.length,
    wonMTD:   won.length,
    lostMTD:  lost.length,
    revenueClosedMTD: won.reduce((s, o) => s + o.value, 0),
    pipelineOwned:    open.reduce((s, o) => s + o.value, 0),
    weightedPipeline: open.reduce((s, o) => s + o.weightedValue, 0),
    winRate90d: (won.length + lost.length) > 0
      ? Math.round((won.length / (won.length + lost.length)) * 100)
      : 0,
  };
}

export const REP_ROLLUPS = REPS.map((r) => rollupRep(r));
export const repsByBranch = (branchId) => REP_ROLLUPS.filter((r) => r.branchId === branchId);

// ------------ Pipeline rollup ------------
const sum = (xs) => xs.reduce((a, b) => a + b, 0);
const avg = (xs) => (xs.length ? sum(xs) / xs.length : 0);

export function rollupPipeline(items) {
  if (!items.length) {
    return {
      count: 0, openCount: 0, bookedCount: 0, lostCount: 0,
      pipelineValue: 0, weightedPipeline: 0, bookedValue: 0,
      avgDealSize: 0, winRate: 0, stalledCount: 0, atRiskCount: 0,
      forecast30d: 0, bigTicketCount: 0,
      stageBreakdown: STAGES.map((s) => ({ ...s, count: 0, value: 0, weightedValue: 0 })),
      sourceBreakdown: SOURCES.map((s) => ({ ...s, count: 0, value: 0 })),
      segmentBreakdown: CUSTOMER_SEGMENTS.map((s) => ({ ...s, count: 0, value: 0 })),
      lostReasonBreakdown: LOST_REASONS.map((l) => ({ ...l, count: 0 })),
    };
  }

  const open    = items.filter((o) => OPEN_STAGE_IDS.includes(o.stageId) && o.outcome !== 'lost');
  const booked  = items.filter((o) => o.stageId === 'booked' || o.outcome === 'won');
  const lost    = items.filter((o) => o.outcome === 'lost');
  const stalled = items.filter((o) => o.isStalled);
  const atRisk  = items.filter((o) => o.isAtRisk);
  const big     = items.filter((o) => o.isBigTicket);
  const closing30d = open.filter((o) => o.expectedCloseDaysAhead >= 0 && o.expectedCloseDaysAhead <= 30);

  return {
    count: items.length,
    openCount:   open.length,
    bookedCount: booked.length,
    lostCount:   lost.length,
    stalledCount: stalled.length,
    atRiskCount:  atRisk.length,
    bigTicketCount: big.length,

    pipelineValue:    sum(open.map((o) => o.value)),
    weightedPipeline: sum(open.map((o) => o.weightedValue)),
    bookedValue:      sum(booked.map((o) => o.value)),
    lostValue:        sum(lost.map((o) => o.value)),
    avgDealSize:      Math.round(avg(items.map((o) => o.value))),

    winRate: (booked.length + lost.length) > 0
      ? Math.round((booked.length / (booked.length + lost.length)) * 100)
      : 0,

    forecast30d: Math.round(sum(closing30d.map((o) => o.weightedValue))),

    avgDaysInStage:        Math.round(avg(open.map((o) => o.daysInStage))),
    avgDaysSinceActivity:  Math.round(avg(open.map((o) => o.daysSinceActivity))),

    stageBreakdown: STAGES.map((s) => {
      const inStage = items.filter((o) => o.stageId === s.id && o.outcome !== 'lost');
      return {
        ...s,
        count: inStage.length,
        value: sum(inStage.map((o) => o.value)),
        weightedValue: sum(inStage.map((o) => o.weightedValue)),
      };
    }),
    sourceBreakdown: SOURCES.map((s) => {
      const from = items.filter((o) => o.sourceId === s.id);
      return {
        ...s,
        count: from.length,
        value: sum(from.map((o) => o.value)),
      };
    }).filter((s) => s.count > 0),
    segmentBreakdown: CUSTOMER_SEGMENTS.map((s) => {
      const inSeg = items.filter((o) => o.customerSegmentId === s.id);
      return {
        ...s,
        count: inSeg.length,
        value: sum(inSeg.map((o) => o.value)),
      };
    }).filter((s) => s.count > 0),
    lostReasonBreakdown: LOST_REASONS.map((l) => ({
      ...l,
      count: lost.filter((o) => o.lostReason === l.id).length,
    })).filter((l) => l.count > 0),
  };
}

export const PIPELINE_GROUP  = rollupPipeline(OPPORTUNITIES);
export const PIPELINE_BRANCH = Object.fromEntries(BRANCHES.map((b) => [b.id, rollupPipeline(opportunitiesByBranch(b.id))]));

// ------------ RENEWALS (expiring contracts next 90d) ------------
function buildRenewals() {
  const out = [];
  let seq = 320;
  const poolBranches = BRANCHES.filter((b) => ['Metro-Flagship', 'Metro-Standard', 'Africa-Metro'].includes(b.peerGroup));

  for (const b of poolBranches) {
    const r = rng(`ren::${b.id}`);
    const n = 2 + intRange(r, 0, 2); // 2-4 renewals per eligible branch
    for (let i = 0; i < n; i++) {
      seq++;
      const seg = pickWeighted(r, CUSTOMER_SEGMENTS.filter((s) => ['insurer', 'fleet', 'dealer', 'builder', 'government'].includes(s.id)));
      const customer = pick(r, CUSTOMER_NAMES[seg.id]);
      const daysAhead = intRange(r, -10, 90);
      const val = Math.round(range(r, seg.valueLo, seg.valueHi));
      const status = daysAhead < 0 ? 'overdue' : daysAhead < 14 ? 'urgent' : daysAhead < 45 ? 'upcoming' : 'scheduled';
      out.push({
        id: `REN-${seq}`,
        customer,
        customerSegmentName: seg.name,
        branchId: b.id,
        branchName: b.name,
        currentValue: val,
        renewalValue: Math.round(val * (1 + (r() - 0.3) * 0.15)), // slight drift up/down
        expiryDate: new Date(now + daysAhead * DAY).toISOString().slice(0, 10),
        daysToExpiry: daysAhead,
        status,
        ownerRep: pick(r, REPS_BY_BRANCH[b.id] || REPS).name,
      });
    }
  }
  return out.sort((a, b) => a.daysToExpiry - b.daysToExpiry);
}

export const RENEWALS = buildRenewals();
export const renewalsByBranch = (branchId) => RENEWALS.filter((r) => r.branchId === branchId);

// ------------ Channel / revenue mix (enhanced Sales_Perf) ------------
export function channelRevenueMix(items = OPPORTUNITIES) {
  const booked = items.filter((o) => o.outcome === 'won' || o.stageId === 'booked');
  return SOURCES.map((s) => {
    const revenue = sum(booked.filter((o) => o.sourceId === s.id).map((o) => o.value));
    return { ...s, revenue };
  }).filter((s) => s.revenue > 0)
    .sort((a, b) => b.revenue - a.revenue);
}

// ------------ 12m revenue series (for trend panel) ------------
const mkSeries = (r, base, drift = 0.012, noise = 0.08) => {
  const out = [];
  let v = base;
  for (let i = 0; i < 12; i++) {
    v = v * (1 + drift + (r() - 0.5) * noise);
    out.push(+v.toFixed(0));
  }
  return out;
};

const trendSeed = rng('sales::trend::group');
export const REVENUE_TRENDS = {
  groupRevenue: mkSeries(trendSeed, 92000000, 0.015, 0.05),
  groupRevenuePriorYear: mkSeries(rng('sales::trend::prior'), 85000000, 0.010, 0.04),
};

export const MONTHS_SALES = ['May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar','Apr'];

// ------------ Formatters ------------
export const fmtR  = (v) => 'R ' + Math.round(v).toLocaleString('en-ZA');
export const fmtRk = (v) => {
  const n = Math.abs(v);
  if (n >= 1e9) return `R ${(v / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `R ${(v / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `R ${Math.round(v / 1e3)}k`;
  return `R ${Math.round(v)}`;
};
export const fmtPct = (v) => `${Math.round(v * 100)}%`;
